// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    baseClassName,
    calendarClassName,
    yearsFromNow,
} from "@werner/App/Components/XngExpirationDatePicker/utils/constants";
import {
    formatValue,
    getCalendarLabel,
    getCalendarUnitMap,
} from "@werner/App/Components/XngExpirationDatePicker/utils/helpers";
import {
    CalendarUnit,
    CalendarView,
    HasAvailableUnits,
} from "@werner/App/Components/XngExpirationDatePicker/utils/types";
import { color } from "@werner/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import styled, { css } from "styled-components";

interface CalendarProps extends HasAvailableUnits {
    calendarState?: "open" | "closed";
    selectedYear?: number;
    selectedMonth?: number;
    disabled?: boolean;
    onMonthClick?: (month: CalendarUnit, selectedYear: number) => void;
    monthFormat?: "numbers" | "names";
}

const CalendarButton = styled.button<InjectableCss>``;

export const YearButton = styled(CalendarButton)`
    ${injectCss}
`;

export const CalendarUnitButtonWrapper = styled(StyledWrapper)`
    justify-content: center;
    display: flex;
    flex-basis: ${100 / 3}%;
    max-width: ${100 / 3}%;
    margin-bottom: 8px;
    ${injectCss}
`;

export const CalendarUnitButton = styled(CalendarButton)<{ disabled: boolean }>`
    max-width: 40px;
    max-height: 40px;
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: transparent;
    color: ${color("text.main")};
    border: 2px solid transparent;
    overflow: hidden;
    opacity: ${({ disabled }) => (disabled ? "0.5" : "1")};
    ${injectCss}
`;
export const NextButton = styled(CalendarButton)`
    ${injectCss}
`;
export const PrevButton = styled(CalendarButton)`
    ${injectCss}
`;

export const Abbr = styled.abbr<InjectableCss>`
    ${injectCss}
`;

const Calendar: FC<CalendarProps> = ({
    calendarState,
    selectedYear,
    selectedMonth,
    onMonthClick,
    availableMonths,
    availableYears,
    monthFormat,
}) => {
    const [view, setView] = useState<CalendarView>("month");
    const [prevDisabled, setPrevDisabled] = useState(false);
    const [nextDisabled, setNextDisabled] = useState(false);
    const [calendarYear, setCalendarYear] = useState<number | undefined>(selectedYear ?? new Date().getFullYear());

    useEffect(() => {
        setCalendarYear(selectedYear);
    }, [selectedYear]);

    useEffect(() => {
        const currentYear = new Date().getFullYear();
        setPrevDisabled(calendarYear === currentYear);
        setNextDisabled(calendarYear === currentYear + yearsFromNow);
    }, [calendarYear]);

    const updateSelectedYear = (offset: number) => {
        const currentYear = new Date().getFullYear();
        const newSelectedYear = (calendarYear || currentYear) + offset;
        const isNextDisabled = newSelectedYear === currentYear + yearsFromNow + 1;
        const isPrevDisabled = newSelectedYear < currentYear;

        if (isNextDisabled || isPrevDisabled) {
            return;
        }

        setCalendarYear(newSelectedYear);
    };

    const handleUnitClick = (unit: CalendarUnit) => {
        if (view === "month") {
            onMonthClick?.(unit, calendarYear!);
            return;
        }
        setCalendarYear(unit.value);
        setView("month");
    };

    const calendarUnitMap = getCalendarUnitMap(view, { availableMonths, availableYears });
    const calendarLabel = getCalendarLabel(view, calendarUnitMap, calendarYear);
    const viewClassName = view === "year" ? `${calendarClassName}__decade-view` : `${calendarClassName}__year-view`;
    const unitsContainerClassName = view === "year" ? `${viewClassName}__years` : `${viewClassName}__months`;
    const unitClassName = view === "year" ? `${unitsContainerClassName}__year` : `${unitsContainerClassName}__month`;
    return (
        <StyledWrapper className={`${baseClassName}__calendar ${baseClassName}__calendar--${calendarState}`}>
            <StyledWrapper className={calendarClassName}>
                <StyledWrapper className={`${calendarClassName}__navigation`}>
                    <PrevButton
                        aria-label="previous year"
                        className={`${calendarClassName}__navigation__arrow ${calendarClassName}__navigation__prev-button`}
                        onClick={() => updateSelectedYear(-1)}
                        disabled={view === "year" || prevDisabled}
                        type="button"
                    >
                        ‹
                    </PrevButton>
                    <YearButton
                        aria-label={`calendar view for ${calendarLabel}`}
                        className={`${calendarClassName}__navigation__label`}
                        onClick={e => {
                            setView("year");
                        }}
                        type="button"
                    >
                        {calendarLabel}
                    </YearButton>
                    <NextButton
                        aria-label="next year"
                        className={`${calendarClassName}__navigation__arrow ${calendarClassName}__navigation__next-button`}
                        onClick={() => updateSelectedYear(1)}
                        disabled={view === "year" || nextDisabled}
                        type="button"
                    >
                        ›
                    </NextButton>
                </StyledWrapper>
                <StyledWrapper className={`${calendarClassName}__viewContainer`}>
                    <StyledWrapper className={`${viewClassName}`}>
                        <StyledWrapper className={`${unitsContainerClassName}`}>
                            {calendarUnitMap.map(unit => {
                                const currentDate = new Date();
                                const isCurrentSelected =
                                    (view === "month" && unit.value === selectedMonth) ||
                                    (view === "year" && unit.value === calendarYear);
                                const selectedClass = isCurrentSelected ? "react-calendar__tile--hasActive" : "";
                                const formattedValue =
                                    view === "month" && monthFormat === "names"
                                        ? unit.key.toString().slice(0, 3)
                                        : formatValue(unit.value);
                                const isUnitDisabled =
                                    view === "month" &&
                                    unit.value < currentDate.getMonth() + 1 &&
                                    calendarYear === currentDate.getFullYear();
                                return (
                                    <CalendarUnitButtonWrapper key={unit.value}>
                                        <CalendarUnitButton
                                            className={`${calendarClassName}__tile ${selectedClass} ${unitClassName}`}
                                            onClick={() => handleUnitClick(unit)}
                                            type="button"
                                            disabled={isUnitDisabled}
                                        >
                                            <Abbr aria-label={`${unit.key} ${view === "year" ? "" : selectedYear}`}>
                                                {formattedValue}
                                            </Abbr>
                                        </CalendarUnitButton>
                                    </CalendarUnitButtonWrapper>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default Calendar;
